import { useEffect, useState } from "react";
import {
  IconCheck,
  IconLanguage,
  IconPencil,
  IconTarget,
  IconUser,
  IconWorld,
  IconX,
  IconPlus,
  IconSparklesFilled,
  IconListCheck,
  IconTrash,
  IconPlug,
  IconTargetArrow,
  IconGenderBigender,
  IconArrowLeft,
} from "@tabler/icons-react";
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Container,
  Grid,
  Group,
  Indicator,
  Loader,
  Paper,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
  Center,
  Select,
  Button,
  ThemeIcon,
  UnstyledButton,
  Image,
  Collapse,
  LoadingOverlay,
} from "@mantine/core";
import { Link, useParams } from "react-router-dom";

// Imports présumés de votre structure
import { editAgent, findAgent } from "../../../../api/AgentsAPI";
import { AVATARS, LANGUAGES, OBJECTIVES, TONES } from "./utils";
import LinkedinAccountSelector from "../../../../components/Selectors/LinkedinAccountSelector";
import EmailSignatureSelector from "../../../../components/Selectors/EmailSignatureSelector";
import EmailAccountSelector from "../../../../components/Selectors/EmailAccountSelector";
import { notifications } from "@mantine/notifications";

// --- MAPPING API -> FRONT ---
function mapApiToFront(apiAgent) {
  return {
    ...apiAgent,
    // Simplification pour l'affichage
    role: apiAgent.bio || "",
    targetsList: apiAgent.targets?.map((t) => t.value) || [],
    valuePropsList: apiAgent.value_propositions?.map((vp) => ({ ...vp })) || [],
    instructionsList: apiAgent.additional_instructions
      ? apiAgent.additional_instructions.split("\n").filter(Boolean)
      : [],
  };
}

// --- MAPPING FRONT -> API (Format exact attendu) ---
function mapFrontToApi(frontAgent) {
  const payload = { ...frontAgent };

  // Re-mapping des champs modifiés
  payload.bio = frontAgent.role;
  payload.additional_instructions = frontAgent.instructionsList.join("\n");

  // Reconstruction des Targets
  payload.targets = frontAgent.targetsList.map((val, idx) => {
    const existing = frontAgent.targets?.find((t) => t.value === val);
    return {
      id: existing ? existing.id : null, // Conserve l'ID si existant
      ai_agent_id: frontAgent.id,
      value: val,
      sorting: idx,
      active: true,
    };
  });

  // Reconstruction des Value Propositions
  payload.value_propositions = frontAgent.valuePropsList.map((vp, idx) => ({
    id: vp.id || null,
    ai_agent_id: frontAgent.id,
    value: vp.value,
    sorting: idx,
    active: vp.active !== undefined ? vp.active : true,
  }));

  // Nettoyage des champs temporaires du front
  delete payload.role;
  delete payload.targetsList;
  delete payload.valuePropsList;
  delete payload.instructionsList;

  return payload;
}

// --- COMPOSANT RÉUTILISABLE POUR LES CARTES D'ÉDITION ---
function FieldCard({
  icon,
  label,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  children,
}) {
  return (
    <Paper
      withBorder
      p="lg"
      radius="xl"
      bg="white"
      style={{
        position: "relative",
        borderColor: isEditing
          ? "var(--mantine-color-primary-4)"
          : "var(--mantine-color-gray-3)",
        boxShadow: isEditing
          ? "0 0 0 1px var(--mantine-color-primary-4)"
          : "0 2px 8px rgba(0,0,0,0.02)",
        transition: "all 0.2s ease",
      }}
    >
      <Group pos="absolute" top={16} right={16} gap={4}>
        {isEditing ? (
          <>
            <ActionIcon
              variant="light"
              color="green"
              onClick={onSave}
              radius="xl"
            >
              <IconCheck size={18} stroke={2} />
            </ActionIcon>
            <ActionIcon
              variant="light"
              color="gray"
              onClick={onCancel}
              radius="xl"
            >
              <IconX size={18} stroke={2} />
            </ActionIcon>
          </>
        ) : (
          <ActionIcon
            variant="subtle"
            color="violet"
            onClick={onEdit}
            radius="xl"
          >
            <IconPencil size={18} stroke={1.5} />
          </ActionIcon>
        )}
      </Group>

      <Group wrap="nowrap" align="flex-start" gap="md">
        {icon && (
          <Box
            w={40}
            h={40}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              backgroundColor: "var(--mantine-color-violet-0)",
              color: "var(--mantine-color-violet-4)",
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}

        <Box style={{ flex: 1, paddingRight: isEditing ? 64 : 32 }}>
          <Text size="sm" fw={600} tt="uppercase" c="dimmed" lts={1} mb={8}>
            {label}
          </Text>
          <Box>{children}</Box>
        </Box>
      </Group>
    </Paper>
  );
}

// --- COMPOSANT PRINCIPAL ---
function EditAgent() {
  const { agentId: id } = useParams();
  const [agent, setAgent] = useState(null);
  const [finding, setFinding] = useState(true);
  const [loading, setLoading] = useState(false);

  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState(null);

  // Pour les ajouts rapides dans les tableaux (ex: Targets)
  const [tempInput, setTempInput] = useState("");

  useEffect(() => {
    if (!id) return;
    setFinding(true);
    // Simulation ou vrai appel API
    findAgent(id)
      .then((res) => setAgent(mapApiToFront(res.data.agent)))
      .catch((err) => console.error(err))
      .finally(() => setFinding(false));
  }, [id]);

  const startEditing = (field) => {
    let val = agent[field];
    if (Array.isArray(val)) val = [...val];
    else if (typeof val === "object" && val !== null) val = { ...val };

    setEditValue(val);
    setEditingField(field);
    setTempInput("");
  };

  const saveEditing = () => {
    setAgent({ ...agent, [editingField]: editValue });
    setEditingField(null);
  };

  const handleSave = (e) => {
    e.preventDefault();
    const payload = mapFrontToApi(agent);

    setLoading(true);
    editAgent({ id: id, data: payload })
      .then((r) => {
        notifications.show({
          title: "Agent mis à jour",
          message: "Les modifications ont été sauvegardées avec succès.",
          color: "green",
        });
      })
      .finally(() => setLoading(false));
  };

  if (finding || !agent) {
    return (
      <Center h="50vh" mx={"auto"}>
        <Loader color="primary" />
      </Center>
    );
  }

  const avatarSrc = AVATARS?.find((a) => a.id === agent.avatar)?.src || "";
  const langLabel =
    LANGUAGES?.find(
      (l) =>
        l.code === agent.spoken_language || l.label === agent.spoken_language,
    )?.label || agent.spoken_language;

  return (
    <Paper
      radius={32}
      p="xl"
      bg="#ffffff66"
      style={{ border: "1px solid var(--mantine-color-gray-2)" }}
      w={"70%"}
      mx={"auto"}
      maw={1200}
      component={"form"}
      onSubmit={handleSave}
    >
      <LoadingOverlay visible={loading} />
      {/* HEADER : Avatar, Nom, Statut */}
      <Box pos="relative" ta="center" mb="xl">
        <Button
          variant="subtle"
          color="gray"
          component={Link}
          to="/agents"
          leftSection={<IconArrowLeft />}
          pos={"absolute"}
          top={0}
          left={0}
        >
          Retour à la liste des agents
        </Button>
        <Box pos="absolute" top={0} right={0}>
          <Badge
            color={agent.active ? "green.4" : "gray.4"}
            variant="light"
            size="xl"
            radius="xl"
            style={{ textTransform: "none", padding: "4px 12px" }}
          >
            <Group gap="sm">
              <Text fw={600} size="sm" c={agent.active ? "dark" : "dimmed"}>
                {agent.active ? "Actif" : "Inactif"}
              </Text>
              <Switch
                checked={agent.active}
                onChange={(e) =>
                  setAgent({ ...agent, active: e.currentTarget.checked })
                }
                color="green.4"
                size="md"
                style={{ cursor: "pointer" }}
              />
            </Group>
          </Badge>
        </Box>

        <Indicator
          inline
          size={22}
          offset={7}
          position="bottom-end"
          color="violet.4"
          withBorder
          label={<IconPencil size={12} />}
          style={{ cursor: "pointer" }}
          onClick={() => startEditing("avatar")}
        >
          <Avatar
            src={avatarSrc}
            size={100}
            radius={100}
            style={{
              border: "3px solid var(--mantine-color-cyan-2)",
              backgroundColor: "var(--mantine-color-gray-1)",
            }}
          />
        </Indicator>

        <Group justify="center" mt="md" gap="xs">
          {editingField === "name" ? (
            <TextInput
              value={editValue}
              onChange={(e) => setEditValue(e.currentTarget.value)}
              onBlur={saveEditing}
              onKeyDown={(e) => e.key === "Enter" && saveEditing()}
              autoFocus
              radius="xl"
              size="md"
            />
          ) : (
            <>
              <Title order={1} fw={600} size="h2">
                {agent.name}
              </Title>
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => startEditing("name")}
                radius="xl"
              >
                <IconPencil size={16} />
              </ActionIcon>
            </>
          )}
        </Group>
      </Box>

      {/* SÉLECTEUR AVATAR (Affiché seulement si en édition) */}
      <Collapse in={editingField === "avatar"}>
        <Paper withBorder p="md" radius="xl" mb="xl">
          <Group justify="space-between" mb="md">
            <Text fw={600}>Choisir un Avatar</Text>
            <ActionIcon
              onClick={() => setEditingField(null)}
              radius="xl"
              color="gray"
              variant="light"
            >
              <IconX size={16} />
            </ActionIcon>
          </Group>
          <Grid gutter="sm">
            {AVATARS?.map((a) => (
              <Grid.Col span={2} key={a.id}>
                <UnstyledButton
                  onClick={() => {
                    setAgent({ ...agent, avatar: a.id });
                    setEditingField(null);
                  }}
                  style={{
                    aspectRatio: "1/1",
                    borderRadius: "50%",
                    overflow: "hidden",
                    border:
                      agent.avatar === a.id
                        ? "3px solid var(--mantine-color-violet-5)"
                        : "3px solid transparent",
                    width: "100%",
                  }}
                >
                  <Image
                    src={a.src}
                    alt={a.name}
                    w="100%"
                    h="100%"
                    fit="cover"
                  />
                </UnstyledButton>
              </Grid.Col>
            ))}
          </Grid>
        </Paper>
      </Collapse>

      {/* CONTENEUR PRINCIPAL DES WIDGETS */}
      <Stack gap="md">
        <Grid gutter="md">
          {/* RÔLE */}
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <FieldCard
              icon={<IconUser size={20} />}
              label="Rôle (Bio)"
              isEditing={editingField === "role"}
              onEdit={() => startEditing("role")}
              onSave={saveEditing}
              onCancel={() => setEditingField(null)}
            >
              {editingField === "role" ? (
                <TextInput
                  value={editValue}
                  onChange={(e) => setEditValue(e.currentTarget.value)}
                  autoFocus
                  onKeyDown={(e) => e.key === "Enter" && saveEditing()}
                  size="lg"
                />
              ) : (
                <Text fw={600}>{agent.role || "Non défini"}</Text>
              )}
            </FieldCard>
          </Grid.Col>

          {/* GENRE */}
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <FieldCard
              icon={<IconGenderBigender size={20} />}
              label="Genre"
              isEditing={editingField === "gender"}
              onEdit={() => startEditing("gender")}
              onSave={saveEditing}
              onCancel={() => setEditingField(null)}
            >
              {editingField === "gender" ? (
                <Select
                  value={editValue}
                  onChange={setEditValue}
                  data={[
                    { value: "female", label: "Féminin" },
                    { value: "male", label: "Masculin" },
                  ]}
                  size="lg"
                />
              ) : (
                <Text fw={600}>
                  {agent.gender === "female"
                    ? "Féminin"
                    : agent.gender === "male"
                      ? "Masculin"
                      : agent.gender}
                </Text>
              )}
            </FieldCard>
          </Grid.Col>

          {/* LANGUE */}
          <Grid.Col span={{ base: 12, sm: 12 }}>
            <FieldCard
              icon={<IconLanguage size={20} />}
              label="Langue"
              isEditing={editingField === "spoken_language"}
              onEdit={() => startEditing("spoken_language")}
              onSave={saveEditing}
              onCancel={() => setEditingField(null)}
            >
              {editingField === "spoken_language" ? (
                <Select
                  value={editValue}
                  onChange={setEditValue}
                  data={LANGUAGES?.map((l) => ({
                    value: l.code,
                    label: l.label,
                  }))}
                  searchable
                  size="lg"
                />
              ) : (
                <Text fw={600}>{langLabel || "Non défini"}</Text>
              )}
            </FieldCard>
          </Grid.Col>
        </Grid>

        {/* TONES */}

        <FieldCard
          icon={<IconWorld size={20} />}
          label="Ton de communication"
          isEditing={editingField === "tone"}
          onEdit={() => startEditing("tone")}
          onSave={saveEditing}
          onCancel={() => setEditingField(null)}
        >
          {editingField === "tone" ? (
            <Grid gutter="sm">
              {TONES.map((t) => {
                const isActive = editValue === t.prompt;
                return (
                  <Grid.Col span={{ base: 12, sm: 6 }} key={t.id}>
                    <UnstyledButton
                      onClick={() => setEditValue(t.prompt)}
                      style={{
                        display: "block",
                        width: "100%",
                        padding: "12px",
                        borderRadius: "var(--mantine-radius-md)",
                        border: `1px solid ${isActive ? "var(--mantine-color-violet-5)" : "var(--mantine-color-gray-3)"}`,
                        backgroundColor: isActive
                          ? "var(--mantine-color-violet-0)"
                          : "white",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <Group wrap="nowrap" align="flex-start">
                        <Box
                          w={20}
                          h={20}
                          style={{
                            borderRadius: 4,
                            border: `1px solid ${isActive ? "transparent" : "var(--mantine-color-gray-4)"}`,
                            backgroundColor: isActive
                              ? "var(--mantine-color-violet-6)"
                              : "transparent",
                            color: "white",
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            marginTop: 2,
                          }}
                        >
                          {isActive && <IconCheck size={14} />}
                        </Box>
                        <Box>
                          <Text
                            size="md"
                            fw={600}
                            c={isActive ? "violet.9" : "dark"}
                          >
                            {t.label}
                          </Text>
                          <Text size="sm" c="dimmed" mt={4} lh={1.4}>
                            {t.description}
                          </Text>
                        </Box>
                      </Group>
                    </UnstyledButton>
                  </Grid.Col>
                );
              })}
            </Grid>
          ) : (
            <Box>
              {/* On cherche si le prompt de l'agent correspond à un prompt connu dans TONES */}
              {(() => {
                const matchedTone = TONES?.find((t) => t.prompt === agent.tone);
                if (matchedTone) {
                  return (
                    <Stack gap={4}>
                      <Badge
                        variant="light"
                        color="violet"
                        size="md"
                        radius="sm"
                      >
                        {matchedTone.label}
                      </Badge>
                      <Text size="md" c="dimmed" fs="italic">
                        "{agent.tone}"
                      </Text>
                    </Stack>
                  );
                }
                // Si c'est un prompt custom (qui ne correspond à aucun de la liste) ou vide
                return (
                  <Text size="sm" fw={500} lh={1.5}>
                    {agent.tone || (
                      <Text component="span" c="dimmed" fs="italic">
                        Non défini
                      </Text>
                    )}
                  </Text>
                );
              })()}
            </Box>
          )}
        </FieldCard>

        {/* CIBLES (Targets) */}
        <FieldCard
          icon={<IconTarget size={20} />}
          label="Cibles Prioritaires"
          isEditing={editingField === "targetsList"}
          onEdit={() => startEditing("targetsList")}
          onSave={saveEditing}
          onCancel={() => setEditingField(null)}
        >
          {editingField === "targetsList" ? (
            <Stack gap="sm">
              <Group gap="sm" wrap="wrap">
                {editValue.map((t, i) => (
                  <Badge
                    key={i}
                    size="lg"
                    fz={12}
                    radius="xl"
                    pr={3}
                    rightSection={
                      <ActionIcon
                        size="sm"
                        color="white"
                        variant="subtle"
                        onClick={() =>
                          setEditValue(editValue.filter((_, idx) => idx !== i))
                        }
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    }
                  >
                    {t}
                  </Badge>
                ))}
              </Group>
              <Group wrap="nowrap">
                <TextInput
                  style={{ flex: 1 }}
                  placeholder="Ajouter une cible..."
                  value={tempInput}
                  onChange={(e) => setTempInput(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (tempInput.trim()) {
                        setEditValue([...editValue, tempInput.trim()]);
                        setTempInput("");
                      }
                    }
                  }}
                />
                <Button
                  variant="light"
                  onClick={() => {
                    if (tempInput.trim()) {
                      setEditValue([...editValue, tempInput.trim()]);
                      setTempInput("");
                    }
                  }}
                >
                  Ajouter
                </Button>
              </Group>
            </Stack>
          ) : (
            <Group gap="xs" mt={4}>
              {agent.targetsList?.map((t, i) => (
                <Badge key={i} variant="outline" color="gray" size="md">
                  {t}
                </Badge>
              ))}
            </Group>
          )}
        </FieldCard>

        {/* VALUE PROPOSITIONS */}
        <FieldCard
          icon={<IconSparklesFilled size={20} />}
          label="Propositions de valeur"
          isEditing={editingField === "valuePropsList"}
          onEdit={() => startEditing("valuePropsList")}
          onSave={saveEditing}
          onCancel={() => setEditingField(null)}
        >
          {editingField === "valuePropsList" ? (
            <Stack gap="sm">
              {editValue.map((vp, i) => (
                <Group key={i} wrap="nowrap">
                  <Switch
                    checked={vp.active !== false}
                    onChange={(e) => {
                      const newVp = [...editValue];
                      newVp[i].active = e.currentTarget.checked;
                      setEditValue(newVp);
                    }}
                    color="violet"
                  />
                  <TextInput
                    style={{ flex: 1 }}
                    value={vp.value}
                    onChange={(e) => {
                      const newVp = [...editValue];
                      newVp[i].value = e.currentTarget.value;
                      setEditValue(newVp);
                    }}
                    size="md"
                  />
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    onClick={() =>
                      setEditValue(editValue.filter((_, idx) => idx !== i))
                    }
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              ))}
              <Button
                variant="outline"
                color="violet"
                onClick={() =>
                  setEditValue([...editValue, { value: "", active: true }])
                }
                leftSection={<IconPlus size={16} />}
              >
                Ajouter une promesse
              </Button>
            </Stack>
          ) : (
            <Stack gap={6} mt={4}>
              {agent.valuePropsList
                ?.filter((vp) => vp.active)
                .map((vp, i) => (
                  <Text key={i} size="md" fw={500} lh={1.5}>
                    • {vp.value}
                  </Text>
                ))}
            </Stack>
          )}
        </FieldCard>

        {/* OBJECTIF */}
        <FieldCard
          icon={<IconTargetArrow size={20} />}
          label="Objectif principal"
          isEditing={editingField === "main_objective"}
          onEdit={() => {
            startEditing("main_objective");
            setTempInput(agent.objective_link || "");
          }}
          onSave={() => {
            setAgent({
              ...agent,
              main_objective: editValue,
              objective_link: tempInput,
            });
            setEditingField(null);
          }}
          onCancel={() => setEditingField(null)}
        >
          {editingField === "main_objective" ? (
            <Stack gap="sm">
              <Select
                value={editValue}
                onChange={setEditValue}
                data={
                  OBJECTIVES?.map((o) => ({
                    value: o.main_objective,
                    label: o.label,
                  })) || []
                }
                placeholder="Choisir un objectif"
                size="lg"
              />
              {OBJECTIVES?.find((o) => o.id === editValue)?.requiresInput && (
                <TextInput
                  label="Lien associé (ex: Calendly)"
                  placeholder="Lien associé (ex: Calendly)"
                  value={tempInput}
                  onChange={(e) => setTempInput(e.currentTarget.value)}
                  size="lg"
                />
              )}
            </Stack>
          ) : (
            <Stack gap={4}>
              <Text size="md" fw={600}>
                {OBJECTIVES?.find(
                  (o) => o.main_objective === agent.main_objective,
                )?.label ||
                  agent.main_objective ||
                  "Aucun objectif"}
              </Text>
              {agent.objective_link && (
                <Text
                  size="md"
                  c="blue"
                  component="a"
                  href={agent.objective_link}
                  target="_blank"
                >
                  {agent.objective_link}
                </Text>
              )}
            </Stack>
          )}
        </FieldCard>

        {/* INSTRUCTIONS */}
        <FieldCard
          icon={<IconListCheck size={20} />}
          label="Consignes de l'agent"
          isEditing={editingField === "instructionsList"}
          onEdit={() => startEditing("instructionsList")}
          onSave={saveEditing}
          onCancel={() => setEditingField(null)}
        >
          {editingField === "instructionsList" ? (
            <Stack gap="sm">
              {editValue.map((inst, index) => (
                <Group key={index} wrap="nowrap" align="flex-start">
                  <ThemeIcon
                    size={28}
                    radius="xl"
                    color="violet.0"
                    c="violet.6"
                    mt={4}
                  >
                    <Text fw={700} size="sm">
                      {index + 1}
                    </Text>
                  </ThemeIcon>
                  <Textarea
                    style={{ flex: 1 }}
                    autosize
                    minRows={1}
                    value={inst}
                    onChange={(e) => {
                      const newInst = [...editValue];
                      newInst[index] = e.currentTarget.value;
                      setEditValue(newInst);
                    }}
                  />
                  <ActionIcon
                    color="gray"
                    variant="subtle"
                    onClick={() =>
                      setEditValue(editValue.filter((_, i) => i !== index))
                    }
                    mt={4}
                  >
                    <IconTrash size={18} />
                  </ActionIcon>
                </Group>
              ))}
              <Button
                variant="outline"
                color="violet.3"
                c="violet.6"
                radius="xl"
                onClick={() => setEditValue([...editValue, ""])}
              >
                + Ajouter une consigne
              </Button>
            </Stack>
          ) : (
            <Stack gap={8}>
              {agent.instructionsList?.map((inst, i) => (
                <Text key={i} size="md" fw={500} lh={1.5}>
                  • {inst}
                </Text>
              ))}
            </Stack>
          )}
        </FieldCard>

        {/* INTÉGRATIONS */}
        <FieldCard
          icon={<IconPlug size={20} />}
          label="Intégrations (Comptes connectés)"
          isEditing={editingField === "integrations"}
          onEdit={() => {
            setEditingField("integrations");
            setEditValue({
              email: agent.integration_id,
              sig: agent.email_signature_id,
              lkin: agent.linkedin_integration_id,
            });
          }}
          onSave={() => {
            setAgent({
              ...agent,
              integration_id: editValue.email,
              email_signature_id: editValue.sig,
              linkedin_integration_id: editValue.lkin,
            });
            setEditingField(null);
          }}
          onCancel={() => setEditingField(null)}
        >
          {editingField === "integrations" ? (
            <Grid gutter="md">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <EmailAccountSelector
                  label="Compte Email"
                  value={editValue.email}
                  onChange={(v) =>
                    setEditValue({ ...editValue, email: v?.id || v })
                  }
                  radius="md"
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <EmailSignatureSelector
                  label="Signature"
                  value={editValue.sig}
                  onChange={(v) =>
                    setEditValue({ ...editValue, sig: v?.id || v })
                  }
                />
              </Grid.Col>
              <Grid.Col span={12}>
                <LinkedinAccountSelector
                  label="Compte LinkedIn"
                  value={editValue.lkin}
                  onChange={(v) =>
                    setEditValue({ ...editValue, lkin: v?.id || v })
                  }
                />
              </Grid.Col>
            </Grid>
          ) : (
            <Group gap="md">
              <Badge
                color={agent.integration_id ? "violet" : "gray"}
                variant="light"
              >
                Email: {agent.integration_id ? "Connecté" : "Non configuré"}
              </Badge>
              <Badge
                color={agent.email_signature_id ? "violet" : "gray"}
                variant="light"
              >
                Signature: {agent.email_signature_id ? "Configurée" : "Aucune"}
              </Badge>
              <Badge
                color={agent.linkedin_integration_id ? "blue" : "gray"}
                variant="light"
              >
                LinkedIn:{" "}
                {agent.linkedin_integration_id ? "Connecté" : "Non configuré"}
              </Badge>
            </Group>
          )}
        </FieldCard>
      </Stack>

      {/* ACTIONS FORMULAIRE */}
      <Group justify="center" mt={"xl"}>
        <Button type="submit" color="violet" radius="xl" size="md">
          Sauvegarder les modifications
        </Button>
      </Group>
    </Paper>
  );
}

export default EditAgent;
